/**
 * User/Profile endpoints (spec §10.4–§10.6) + the public card (user-detail.md).
 */
import { env } from '../lib/env';
import {
  completeOnboardingResponseSchema,
  publicCardSchema,
  userProfileSchema,
  type CompleteOnboardingRequest,
  type CompleteOnboardingResponse,
  type PublicCardResponse,
  type UpdateProfileRequest,
  type UserProfile,
} from './apiSchemas';
import { httpClient } from './httpClient';

export function getMe(signal?: AbortSignal): Promise<UserProfile> {
  return httpClient.get('/users/me', userProfileSchema, signal);
}

export function updateProfile(body: UpdateProfileRequest): Promise<UserProfile> {
  return httpClient.patch('/users/me', { schema: userProfileSchema, body });
}

export function completeOnboarding(
  body: CompleteOnboardingRequest,
): Promise<CompleteOnboardingResponse> {
  return httpClient.patch('/users/me/onboarding', {
    schema: completeOnboardingResponseSchema,
    body,
  });
}

/** Thrown when a public card handle does not resolve (404 / unpublished). */
export class PublicCardNotFound extends Error {
  constructor() {
    super('PUBLIC_CARD_NOT_FOUND');
    this.name = 'PublicCardNotFound';
  }
}

/**
 * Fetches a user's PUBLIC card (user-detail.md). Deliberately uses `credentials: 'omit'`
 * — the endpoint is any-origin (`Access-Control-Allow-Origin: *`) and sending cookies
 * would trip CORS. No CSRF, no session; safe for anonymous visitors.
 */
export async function getPublicCard(
  handle: string,
  signal?: AbortSignal,
): Promise<PublicCardResponse> {
  const res = await fetch(`${env.apiBaseUrl}/public/users/${encodeURIComponent(handle)}`, {
    method: 'GET',
    credentials: 'omit',
    headers: { Accept: 'application/json' },
    signal,
  });
  if (res.status === 404) throw new PublicCardNotFound();
  if (!res.ok) throw new Error(`PUBLIC_CARD_FETCH_FAILED_${res.status}`);
  const body = (await res.json()) as { data?: unknown };
  return publicCardSchema.parse(body.data);
}
